import type { Notice } from "../services/api/types";

export type NoticeWithEventSchedule = Pick<
  Notice,
  | "id"
  | "noticeId"
  | "title"
  | "eventStartDate"
  | "eventStartTime"
  | "eventEndDate"
  | "eventEndTime"
>;

/** Parse YYYY-MM-DD (and optional time) in the local timezone. */
export function parseNoticeEventLocalDate(
  dateString: string,
  timeString?: string
): Date {
  if (timeString) {
    return new Date(`${dateString}T${timeString}`);
  }
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export type NoticeEventBounds = {
  start: Date;
  end: Date;
  allDay: boolean;
};

/** Returns start/end/all-day for a notice with an event schedule, or null if not mappable. */
export function getNoticeEventBounds(
  notice: NoticeWithEventSchedule
): NoticeEventBounds | null {
  if (!notice.eventStartDate) {
    return null;
  }

  const start = notice.eventStartTime
    ? parseNoticeEventLocalDate(notice.eventStartDate, notice.eventStartTime)
    : parseNoticeEventLocalDate(notice.eventStartDate);

  const rawEnd = notice.eventEndDate
    ? notice.eventEndTime
      ? parseNoticeEventLocalDate(notice.eventEndDate, notice.eventEndTime)
      : parseNoticeEventLocalDate(notice.eventEndDate)
    : start;

  const allDay = !notice.eventStartTime;
  // All-day events end at the start of the day *after* their last day (an
  // exclusive boundary, which react-big-calendar also expects). Otherwise a
  // 10–13 March event renders on 10–12 only and reads as "past" from the
  // start of the 13th. Applies whenever the end is a bare date, not just
  // single-day events. Built from calendar fields, not +24h, so it stays
  // on midnight across a DST change.
  const end =
    allDay && !(notice.eventEndDate && notice.eventEndTime)
      ? new Date(rawEnd.getFullYear(), rawEnd.getMonth(), rawEnd.getDate() + 1)
      : rawEnd;

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }

  return {
    start,
    end,
    allDay,
  };
}

export type NoticeCalendarEventKind = "event" | "publish";

export type NoticeCalendarEventCore<TNotice extends Notice = Notice> = {
  start: Date;
  end: Date;
  allDay: boolean;
  noticeId: string;
  eventKey: string;
  kind: NoticeCalendarEventKind;
  notice: TNotice;
  fullTitle: string;
  isStaffOnly: boolean;
};

export function resolveNoticeCalendarId(notice: NoticeWithEventSchedule): string {
  return notice.noticeId || notice.id;
}

export function resolveNoticeCalendarTitle(notice: NoticeWithEventSchedule): string {
  return notice.title?.trim() || "Event";
}

/** Map notices that have event dates into calendar-ready core fields. */
export function mapNoticesToCalendarEventCores<TNotice extends Notice>(
  notices: TNotice[] | undefined | null
): NoticeCalendarEventCore<TNotice>[] {
  if (!notices?.length) {
    return [];
  }

  const events: NoticeCalendarEventCore<TNotice>[] = [];

  for (const notice of notices) {
    const bounds = getNoticeEventBounds(notice);
    if (!bounds) {
      continue;
    }

    const noticeId = resolveNoticeCalendarId(notice);
    events.push({
      ...bounds,
      noticeId,
      eventKey: `${noticeId}-event`,
      kind: "event",
      notice,
      fullTitle: resolveNoticeCalendarTitle(notice),
      isStaffOnly: notice.staffOnly === true,
    });
  }

  return events;
}

/** Map notices with a future publishAt into calendar "send" markers for staff view. */
export function mapNoticesToPublishCalendarEventCores<TNotice extends Notice>(
  notices: TNotice[] | undefined | null,
  now: Date = new Date()
): NoticeCalendarEventCore<TNotice>[] {
  if (!notices?.length) {
    return [];
  }

  const events: NoticeCalendarEventCore<TNotice>[] = [];
  const nowMs = now.getTime();

  for (const notice of notices) {
    if (notice.publishStatus !== "scheduled" || !notice.publishAt) {
      continue;
    }

    const publishAt = new Date(notice.publishAt);
    if (Number.isNaN(publishAt.getTime()) || publishAt.getTime() <= nowMs) {
      continue;
    }

    const noticeId = resolveNoticeCalendarId(notice);
    const title = resolveNoticeCalendarTitle(notice);
    events.push({
      start: publishAt,
      end: publishAt,
      allDay: false,
      noticeId,
      eventKey: `${noticeId}-publish`,
      kind: "publish",
      notice,
      fullTitle: title,
      isStaffOnly: notice.staffOnly === true,
    });
  }

  return events;
}

export function isCalendarEventPast(end: Date, now: Date = new Date()): boolean {
  return end < now;
}
